import {
  QuizComment,
  QuizQuestion,
  QuizStatus,
  QuizQuestionFile,
} from 'apis/entities/quiz.entity'
import tickSvg from 'images/quiz-tick.svg'
import crossSvg from 'images/quiz-cross.svg'
import minusSvg from 'images/quiz-minus.svg'
import plusSvg from 'images/quiz-plus.svg'
import { StyleUtil } from 'utils/StyleUtil'
import { useState, KeyboardEvent, useEffect, useCallback } from 'react'
import { ToastUtil } from 'utils/ToastUtil'
import React from 'react'
import { Switch } from 'components/Switch'
import ButtonSpinner from './ButtonSpinner'
import { TextareaAutosize } from '@mui/base'
import UploadMedia from './UploadMedia'
import { getExtensionIcon, isAudio, isImage } from 'utils/FileUtil'
import { Tooltip } from 'react-tooltip'
import { updateQuestion } from 'app/QuizSlice'
import { useAppSelector, useAppDispatch } from 'app/hooks'
import Emitter, { Events } from 'core/emitter'
import Svg, { Icon, IconClass } from 'components/Svg'
import { useQuizApi } from 'providers/QuizApiProvider'

type QuestionEditViewProps = {
  index: number
  question: QuizQuestion
  isLocked: boolean
  onQuestionUp: (question: QuizQuestion) => void
  onQuestionDown: (question: QuizQuestion) => void
  onQuestionDelete: (question: QuizQuestion) => void
  onQuestionDeleted: (question: QuizQuestion) => void
  onQuestionMove: () => void
  onQuestionRegenerate: (question: QuizQuestion, approved: boolean) => void
  dragHandleProps: any
}

export default function QuestionEditView({
  index,
  question,
  isLocked,
  onQuestionUp,
  onQuestionDown,
  onQuestionDelete,
  onQuestionDeleted,
  onQuestionMove,
  onQuestionRegenerate,
  dragHandleProps,
}: QuestionEditViewProps) {
  const quiz = useAppSelector((state) => state.quiz.value)
  // set to edit mode if question is new
  const [isEdit, setIsEdit] = useState(
    question.text === null || question.answers === null,
  )
  const [isLoading, setIsLoading] = useState(false)
  const {
    updateQuizQuestion,
    addQuizQuestionFile,
    deleteQuizQuestionFile,
    deleteQuizQuestion,
    regenerateQuestion,
  } = useQuizApi()
  const refQuestion = React.createRef<HTMLTextAreaElement>()
  const refLearningOutcome = React.createRef<HTMLTextAreaElement>()

  const [questionText, setQuestionText] = useState(question.text || '')
  const [refQuestionValue, setRefQuestionValue] = useState(question.text || '')
  const [learningOutcome, setLearningOutcome] = useState(
    question.learningOutcome || '',
  )
  const [archTopic, setArchTopic] = useState<any | null>(
    question.archTopic || null,
  )

  const [refLearningOutcomeValue, setRefLearningOutcomeValue] = useState(
    question.learningOutcome || '',
  )

  // dynamic ref array for textarea elements, default to 3 placeholders
  const refAnswers = Array.from({ length: question.answers?.length || 3 }).map(
    () => {
      return { ref: React.createRef<HTMLTextAreaElement>() }
    },
  )
  const [answerRefs, setAnswerRefs] = useState(refAnswers)
  // default to 3 placeholders
  const [answers, setAnswers] = useState(question.answers || ['', '', ''])

  const [isCurrentVersion, setIsCurrentVersion] = useState(true)

  const [currentQuestionText, setCurrentQuestionText] = useState(
    question.text || '',
  )
  const [currentLearningOutcome, setCurrentLearningOutcome] = useState(
    question.learningOutcome || '',
  )
  const [currentAnswers, setCurrentAnswers] = useState(question.answers || [])
  const [edited, setEdited] = useState(question.edited || false)

  const [currentFiles, setCurrentFiles] = useState(question.files || [])
  const [currentArchTopic, setCurrentArchTopic] = useState(
    question.archTopic || null,
  )
  const [mediaFile, setMediaFile] = useState<
    QuizQuestionFile | undefined | null
  >(currentFiles[0])

  const hasFiles = currentFiles.length > 0

  const [isMinimizedUploadMedia, setIsMinimizedUploadMedia] = useState(
    !hasFiles,
  )

  // useRef to store a timer
  const timerRef = React.useRef<any>()

  const dispatch = useAppDispatch()

  const [isRegenerating, setIsRegenerating] = useState(false)

  const hasPreviousVersion = () => {
    return edited && question.previous != null
  }

  const isAllApproved = (): boolean => {
    if (quiz.status === QuizStatus.Draft) return false
    // get all reviewers
    const reviewers = quiz.reviewers.filter(
      (reviewer) => reviewer.version === quiz.version,
    )
    // console.log('reviewers', reviewers)
    // get all comments
    const comments = question.comments || []

    // check if all reviewers approved
    return reviewers.every((reviewer) => {
      return comments
        .filter((comment) => comment.version === quiz.version)
        .some((comment) => {
          return comment.reviewer.id === reviewer.id && comment.approved
        })
    })
  }

  const [isMinimized, setIsMinimized] = useState(isAllApproved())

  const onClickEdit = () => {
    setIsEdit(true)
    setIsMinimizedUploadMedia(!hasFiles)
  }

  const tooltipStyle = {
    backgroundColor: '#fff',
    color: '#000',
    borderRadius: '6px',
  }

  const onClickSave = async (auto: boolean = false) => {
    try {
      // console.log(refQuestionValue, refLearningOutcomeValue, question.id)
      // console.log('timerRef', timerRef.current)
      // console.log(answerRefs.map((ref) => ref.ref.current?.value))
      if (auto) {
        if (refQuestionValue === undefined) return
        if (refLearningOutcomeValue === undefined) return
        // lose focus
        if (answerRefs.some((ref) => ref.ref.current?.value === undefined)) {
          return
        }
      }
      console.log('onClickSave::auto', auto)
      // stop timer
      if (timerRef.current) {
        console.log('clear timer')
        clearTimeout(timerRef.current)
      }

      if (!question.id) return
      if (answerRefs.length === 0) return
      if (refQuestionValue === '') {
        if (!auto) {
          ToastUtil.warning('Question cannot be empty')
          return
        }
      }
      if (refLearningOutcomeValue === '') {
        if (!auto) {
          ToastUtil.warning('Learning outcome cannot be empty')
          return
        }
      }
      if (
        answerRefs.some(
          (ref) => ref.ref.current?.value === '' || !ref.ref.current?.value,
        )
      ) {
        if (!auto) {
          ToastUtil.warning('Answers cannot be empty')
          return
        }
      }

      const payload = {
        text: refQuestionValue,
        answers: answerRefs.map((ref) => ref.ref.current?.value),
        learningOutcome: refLearningOutcomeValue,
      } as QuizQuestion

      // compare payload with original question
      const isQuestionChanged =
        payload.text !== questionText ||
        payload.answers?.join('') !== question.answers?.join('') ||
        payload.learningOutcome !== learningOutcome

      if (!isQuestionChanged) {
        setIsEdit(false)
        return
      }

      setIsLoading(true)
      await updateQuizQuestion(quiz.id, question.id, payload)
      setIsLoading(false)
      if (!auto) {
        setIsEdit(false)
        ToastUtil.success('Question saved')
      }
      // update current question
      setQuestionText(payload.text || '')
      setLearningOutcome(payload.learningOutcome || '')
      setAnswers(payload.answers || [])
      // for switch toggle
      setCurrentQuestionText(payload.text || '')
      setCurrentLearningOutcome(payload.learningOutcome || '')
      setCurrentAnswers(payload.answers || [])
      setCurrentArchTopic(payload.archTopic || null)
      // for auto save reference
      setRefQuestionValue(payload.text || '')
      setRefLearningOutcomeValue(payload.learningOutcome || '')
      // enable switch if not enabled
      setEdited(true)
      // update question
      const newQuestion = { ...question, ...payload, edited: true }
      dispatch(updateQuestion({ question: newQuestion }))
    } catch (error) {
      console.log(error)
      setIsLoading(false)
      if (!auto) {
        ToastUtil.error('Failed to save question')
      }
    }
  }

  const onClickRegenerate = async () => {
    const approved = isAllApproved()
    if (approved) {
      onQuestionRegenerate(question, approved)
    } else {
      onConfirmRegenerate({ questionId: question.id })
    }
  }

  const onConfirmRegenerate = async (data: { questionId: string }) => {
    const { questionId } = data
    if (questionId !== question.id) return
    try {
      // stong hold the refs before async call and UI state change
      const rQuestion = refQuestion.current
      const rLearningOutcome = refLearningOutcome.current
      const rAnswers = answerRefs.map((ref) => ref.ref.current)

      // for instant data to regenerate
      const currentLearningOutcome = rLearningOutcome?.value || ''

      if (quiz.documentIds === null || quiz.documentIds.length === 0) {
        if (!question.learningOutcome && !currentLearningOutcome) {
          ToastUtil.warning('Learning outcome required')
          return
        }
      }
      // stop auto save
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }

      setIsRegenerating(true)
      const regeneratedQuestion = await regenerateQuestion(
        question.id,
        currentLearningOutcome,
      )
      if (regeneratedQuestion) {
        // just update the question and answers editable fields
        rQuestion!.value = regeneratedQuestion.text || ''
        // don't change the learning outcome
        // rLearningOutcome!.value = regeneratedQuestion.learningOutcome || ''

        // set the answers by existing answer length, don't change the refs
        const newAnswers: string[] = []
        for (let i = 0; i < rAnswers.length; i++) {
          rAnswers[i]!.value = regeneratedQuestion.answers![i]
          newAnswers.push(regeneratedQuestion.answers![i])
        }
        setAnswers(newAnswers)

        // update refAnswers style
        answerRefs.forEach((ref, i) => {
          if (ref.ref.current) {
            if (newAnswers[i] === '') {
              ref.ref.current.style.color = '#92929d'
            } else {
              ref.ref.current.style.color = '#000'
            }
          }
        })

        // update the state value
        setQuestionText(regeneratedQuestion.text || '')
        setRefQuestionValue(regeneratedQuestion.text || '')
        // don't change the learning outcome
        // setLearningOutcome(regeneratedQuestion.learningOutcome || '')
        // setRefLearningOutcomeValue(regeneratedQuestion.learningOutcome || '')

        ToastUtil.success('Question regenerated')
      } else {
        ToastUtil.error('Failed to regenerate question')
      }
    } catch (error) {
      console.error(error)
      ToastUtil.error('Failed to regenerate question')
    } finally {
      setIsRegenerating(false)
    }
  }

  const onClickMinus = () => {
    setIsMinimized(!isMinimized)
  }

  const onChangeSwitch = useCallback(
    (checked: boolean) => {
      setIsCurrentVersion(checked)
      if (checked) {
        // use current version
        setQuestionText(currentQuestionText || '')
        setLearningOutcome(currentLearningOutcome || '')
        setArchTopic(currentArchTopic || null)
        setAnswers(currentAnswers || [])
        // for auto save reference
        setRefQuestionValue(currentQuestionText || '')
        setRefLearningOutcomeValue(currentLearningOutcome || '')
      } else {
        // use previous version
        setQuestionText(question.previous?.text || question.text || '')
        setLearningOutcome(
          question.previous?.learningOutcome || question.learningOutcome || '',
        )
        setArchTopic(question.previous?.archTopic || null)
        setAnswers(question.previous?.answers || question.answers || [])
        // off the edit mode
        setIsEdit(false)
      }
    },
    [
      currentQuestionText,
      currentLearningOutcome,
      currentArchTopic,
      currentAnswers,
      question.previous?.text,
      question.previous?.learningOutcome,
      question.previous?.archTopic,
      question.previous?.answers,
      question.text,
      question.learningOutcome,
      question.answers,
    ],
  )

  const getDateTimeString = (date: string) => {
    // example: 1:40pm 27 Jun 23
    const dateObject = new Date(date)
    const hours = dateObject.getHours()
    const minutes = dateObject.getMinutes()
    const ampm = hours >= 12 ? 'pm' : 'am'
    const day = dateObject.getDate()
    const month = dateObject.toLocaleString('default', { month: 'short' })
    const year = dateObject.getFullYear().toString().substring(2)
    return `${hours}:${minutes}${ampm} ${day} ${month} ${year}`
  }

  const renderComment = (comment: QuizComment) => {
    if (comment.approved) {
      return (
        <>
          <p className="quiz-text-approved">
            Approved by {comment.reviewer.email} {' on '}
            {getDateTimeString(comment.createdAt)}
          </p>
          {comment.text && (
            <div className="quiz-comment-container">
              <p className="">Comments:</p>
              <p className="">{comment.text}</p>
            </div>
          )}
        </>
      )
    } else {
      return (
        <>
          <p className="quiz-text-rejected">
            Rejected by {comment.reviewer.email} {' on '}
            {getDateTimeString(comment.createdAt)}
          </p>
          {comment.text && (
            <div className="quiz-comment-container">
              <p className="">Comments:</p>
              <p className="">{comment.text}</p>
            </div>
          )}
        </>
      )
    }
  }

  const isEditable = () => {
    return isCurrentVersion
  }

  const handleKeyDownForTextArea = (
    event: KeyboardEvent<HTMLTextAreaElement>,
  ) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      onClickSave()
    } else if (event.key === 'Delete') {
      // TODO: delete the answer
      // event.preventDefault()
      // onRemoveAnswerByElementId(event.currentTarget.id)
    }
  }

  const handleKeyUpForTextArea = (
    event: React.KeyboardEvent<HTMLTextAreaElement>,
  ) => {
    // check if target is empty, change color
    if (event.currentTarget.value === '') {
      event.currentTarget.style.color = '#92929d'
    } else {
      event.currentTarget.style.color = '#000'
    }

    const id = event.currentTarget.id
    if (id === 'question') {
      setRefQuestionValue(event.currentTarget.value)
    } else if (id === 'learning-outcome') {
      setRefLearningOutcomeValue(event.currentTarget.value)
    }

    // set timeout 5 seconds to save the question
    if (event.key !== 'Enter' && isEdit) {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
      timerRef.current = setTimeout(async () => {
        if (isEdit) {
          console.log('auto save', question.id)
          await onClickSave(true)
        }
      }, 5000)
    }
  }

  const onAddedFile = async (
    url: string,
    type: string,
    name: string,
    size: number,
  ) => {
    if (!quiz.id || !question.id) return
    const addedFile = await addQuizQuestionFile(
      quiz.id,
      question.id,
      url,
      type,
      name,
      size,
      quiz.version,
    )
    // console.log('added', addedFile)
    // update current files
    setCurrentFiles([...currentFiles, addedFile])
    setMediaFile(addedFile)
    ToastUtil.success('Uploaded successfully')
  }

  const onRemoveFile = async (url: string) => {
    if (!quiz.id || !question.id) return
    const fileId = currentFiles.find((file) => file.url === url)?.id
    if (!fileId) return
    const result = await deleteQuizQuestionFile(quiz.id, question.id, fileId)
    if (result.success) {
      // update current files
      setCurrentFiles(currentFiles.filter((file) => file.url !== url))
      setMediaFile(null)
      ToastUtil.success('File removed')
    } else {
      ToastUtil.error('Failed to remove file')
    }
  }

  const onClickUp = () => {
    // move up
    if (isRegenerating) return
    onQuestionUp(question)
  }

  const onClickDown = () => {
    // move down
    if (isRegenerating) return
    onQuestionDown(question)
  }

  const onMouseDownMove = (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    // console.log('onMouseDownMove')
    e.stopPropagation()
    onQuestionMove()
  }

  const onClickTrash = async () => {
    if (isLocked || isLoading || isRegenerating) return
    // check if question is all reviewers approved in the current version
    // check if all reviewers approved
    if (isAllApproved()) {
      // show confirmation dialog from parent
      onQuestionDelete(question)
    } else {
      // delete it diecctly
      onConfirmDelete()
    }
  }

  const onConfirmDelete = async () => {
    setIsLoading(true)
    const reuslt = await deleteQuizQuestion(quiz.id, question.id)
    if (reuslt.success) {
      onQuestionDeleted(question)
      ToastUtil.success('Question deleted')
    } else {
      ToastUtil.error('Failed to delete question')
    }
    setIsLoading(false)
  }

  const onAddAnswer = () => {
    if (isRegenerating) return
    // add ref for new answer
    const newRef = React.createRef<HTMLTextAreaElement>()
    setAnswerRefs([...answerRefs, { ref: newRef }])
    setAnswers([...answers, ''])
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const onRemoveAnswerByElementId = (elementId: String) => {
    if (isRegenerating) return
    // remove the last answer
    if (answers.length === 0) return

    // minimum 2 answers
    if (answers.length === 2) {
      ToastUtil.warning('Minimum 2 answers required')
      return
    }

    // get the index from the element id
    const index = parseInt(elementId.split('-')[1])
    if (index === -1) return
    if (index === 0) {
      ToastUtil.warning('Cannot remove the correct answer')
      return
    }

    // remove answer by the index
    const newAnswers = answers.filter((_, i) => i !== index)
    // console.log('newAnswers', newAnswers)
    setAnswers(newAnswers)

    // remove the last ref
    const newRefs = answerRefs.slice(0, answerRefs.length - 1)

    // update ref values for the new answers
    newRefs.forEach((ref, i) => {
      ref.ref.current!.value = newAnswers[i]
    })

    setAnswerRefs(newRefs)
  }

  const onRemoveAnswer = () => {
    if (isRegenerating) return
    // remove the last answer
    if (answers.length === 0) return

    // minimum 2 answers
    if (answers.length === 2) {
      ToastUtil.warning('Minimum 2 answers required')
      return
    }

    // remove ref for the last answer
    const newRefs = answerRefs.slice(0, answerRefs.length - 1)
    setAnswerRefs(newRefs)
    setAnswers(answers.slice(0, answers.length - 1))
  }

  const onUpdatedQuizQuestionTopic = (data: {
    questionId: string
    question: any
  }) => {
    if (data.questionId === question.id) {
      // update the topic link and name
      console.log('onUpdatedQuizQuestionTopic', data)
      setArchTopic(data.question.archTopic)
    }
  }

  useEffect(() => {
    // set the current version when approved
    if (isLocked) {
      onChangeSwitch(true)
    }
  }, [isLocked, onChangeSwitch])

  useEffect(() => {
    Emitter.on(Events.RegenerateQuestion, onConfirmRegenerate)
    Emitter.on(Events.UpdatedQuizQuestionTopic, onUpdatedQuizQuestionTopic)
    return () => {
      Emitter.off(Events.RegenerateQuestion, onConfirmRegenerate)
      Emitter.off(Events.UpdatedQuizQuestionTopic, onUpdatedQuizQuestionTopic)
    }
  })

  return (
    <div
      className="flex flex-col gap-2 bg-white p-1"
      data-testid={`question-item-${question.id}`}
    >
      <div className="flex flex-row pl-2 pr-4">
        <div className="flex grow flex-row gap-[12px] pr-4">
          <p className="quiz-question">{index + 1}.</p>
          {!isEdit && (
            <p className="quiz-question grow pr-4">
              {
                // convert \n to <br /> and render
                questionText.split('\n').map((line, index) => (
                  <React.Fragment key={index}>
                    {line}
                    <br />
                  </React.Fragment>
                ))
              }
            </p>
          )}
          {isEdit && (
            <TextareaAutosize
              id="question"
              data-testid="textarea-question"
              className={`quiz-editable-question ${
                questionText.length === 0
                  ? 'quiz-editable-empty'
                  : 'quiz-editable-text'
              }`}
              defaultValue={questionText}
              ref={refQuestion}
              onKeyDown={handleKeyDownForTextArea}
              onKeyUp={handleKeyUpForTextArea}
              placeholder="Type question here"
            />
          )}
        </div>
        <div className="flex flex-row items-center gap-2">
          {!isLocked && (
            <>
              <svg
                width="17"
                height="16"
                viewBox="0 0 17 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="button-svg"
                onClick={onClickUp}
                data-tooltip-id="tooltip-move-up"
                data-testid="btn-move-up"
                tabIndex={-1}
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M8.02779 5.52876C8.15281 5.40378 8.32234 5.33357 8.49912 5.33357C8.6759 5.33357 8.84544 5.40378 8.97045 5.52876L12.7418 9.3001C12.8055 9.36159 12.8562 9.43516 12.8912 9.51649C12.9261 9.59783 12.9445 9.68531 12.9453 9.77383C12.9461 9.86235 12.9292 9.95014 12.8957 10.0321C12.8621 10.114 12.8126 10.1884 12.7501 10.251C12.6875 10.3136 12.613 10.3631 12.5311 10.3966C12.4492 10.4302 12.3614 10.447 12.2729 10.4463C12.1843 10.4455 12.0969 10.4271 12.0155 10.3922C11.9342 10.3572 11.8606 10.3064 11.7991 10.2428L8.49912 6.94276L5.19912 10.2428C5.07338 10.3642 4.90498 10.4314 4.73019 10.4299C4.55539 10.4284 4.38818 10.3582 4.26457 10.2346C4.14097 10.111 4.07086 9.94383 4.06934 9.76903C4.06782 9.59423 4.13501 9.42583 4.25645 9.3001L8.02779 5.52876Z"
                />
              </svg>
              <svg
                width="17"
                height="16"
                viewBox="0 0 17 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="button-svg"
                onClick={onClickDown}
                data-tooltip-id="tooltip-move-down"
                data-testid="btn-move-down"
                tabIndex={-1}
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M8.97221 10.4712C8.84719 10.5962 8.67766 10.6664 8.50088 10.6664C8.3241 10.6664 8.15456 10.5962 8.02955 10.4712L4.25821 6.6999C4.19454 6.63841 4.14375 6.56484 4.10881 6.48351C4.07387 6.40217 4.05548 6.31469 4.05471 6.22617C4.05394 6.13765 4.07081 6.04986 4.10433 5.96793C4.13785 5.886 4.18735 5.81157 4.24995 5.74897C4.31254 5.68638 4.38698 5.63688 4.46891 5.60336C4.55084 5.56983 4.63863 5.55297 4.72715 5.55374C4.81567 5.55451 4.90315 5.5729 4.98448 5.60784C5.06582 5.64277 5.13938 5.69356 5.20088 5.75724L8.50088 9.05724L11.8009 5.75724C11.9266 5.6358 12.095 5.5686 12.2698 5.57012C12.4446 5.57164 12.6118 5.64175 12.7354 5.76536C12.859 5.88896 12.9291 6.05617 12.9307 6.23097C12.9322 6.40577 12.865 6.57417 12.7435 6.6999L8.97221 10.4712Z"
                />
              </svg>
              {!isRegenerating && (
                <div {...dragHandleProps} tabIndex={-1}>
                  <svg
                    id="svg-move-question"
                    width="17"
                    height="16"
                    viewBox="0 0 17 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="button-svg-fill-black"
                    onMouseDown={onMouseDownMove}
                    data-tooltip-id="tooltip-move-question"
                    data-testid="btn-move-question"
                    tabIndex={-1}
                  >
                    <path d="M8.14689 0.145917C8.19334 0.0993539 8.24852 0.0624112 8.30926 0.0372047C8.37001 0.0119983 8.43513 -0.000976563 8.50089 -0.000976562C8.56666 -0.000976563 8.63178 0.0119983 8.69253 0.0372047C8.75327 0.0624112 8.80845 0.0993539 8.85489 0.145917L10.8549 2.14592C10.9488 2.2398 11.0015 2.36714 11.0015 2.49992C11.0015 2.63269 10.9488 2.76003 10.8549 2.85392C10.761 2.9478 10.6337 3.00055 10.5009 3.00055C10.3681 3.00055 10.2408 2.9478 10.1469 2.85392L9.00089 1.70692V4.49992C9.00089 4.63252 8.94821 4.7597 8.85445 4.85347C8.76068 4.94724 8.6335 4.99992 8.50089 4.99992C8.36828 4.99992 8.24111 4.94724 8.14734 4.85347C8.05357 4.7597 8.00089 4.63252 8.00089 4.49992V1.70692L6.85489 2.85392C6.80841 2.90041 6.75322 2.93728 6.69248 2.96244C6.63174 2.9876 6.56664 3.00055 6.50089 3.00055C6.43515 3.00055 6.37005 2.9876 6.30931 2.96244C6.24857 2.93728 6.19338 2.90041 6.14689 2.85392C6.10041 2.80743 6.06353 2.75224 6.03837 2.6915C6.01321 2.63076 6.00026 2.56566 6.00026 2.49992C6.00026 2.43417 6.01321 2.36907 6.03837 2.30833C6.06353 2.24759 6.10041 2.1924 6.14689 2.14592L8.14689 0.145917ZM8.50089 9.99992C9.03133 9.99992 9.54003 9.7892 9.91511 9.41413C10.2902 9.03906 10.5009 8.53035 10.5009 7.99992C10.5009 7.46948 10.2902 6.96078 9.91511 6.5857C9.54003 6.21063 9.03133 5.99992 8.50089 5.99992C7.97046 5.99992 7.46175 6.21063 7.08668 6.5857C6.71161 6.96078 6.50089 7.46948 6.50089 7.99992C6.50089 8.53035 6.71161 9.03906 7.08668 9.41413C7.46175 9.7892 7.97046 9.99992 8.50089 9.99992ZM8.50089 8.99992C8.23568 8.99992 7.98132 8.89456 7.79379 8.70702C7.60625 8.51949 7.50089 8.26513 7.50089 7.99992C7.50089 7.7347 7.60625 7.48035 7.79379 7.29281C7.98132 7.10527 8.23568 6.99992 8.50089 6.99992C8.76611 6.99992 9.02046 7.10527 9.208 7.29281C9.39554 7.48035 9.50089 7.7347 9.50089 7.99992C9.50089 8.26513 9.39554 8.51949 9.208 8.70702C9.02046 8.89456 8.76611 8.99992 8.50089 8.99992ZM14.3549 10.3539L16.3549 8.35392C16.4015 8.30747 16.4384 8.2523 16.4636 8.19155C16.4888 8.13081 16.5018 8.06568 16.5018 7.99992C16.5018 7.93415 16.4888 7.86903 16.4636 7.80828C16.4384 7.74754 16.4015 7.69236 16.3549 7.64592L14.3549 5.64592C14.261 5.55203 14.1337 5.49929 14.0009 5.49929C13.8681 5.49929 13.7408 5.55203 13.6469 5.64592C13.553 5.7398 13.5003 5.86714 13.5003 5.99992C13.5003 6.13269 13.553 6.26003 13.6469 6.35392L14.7939 7.49992H12.0009C11.8683 7.49992 11.7411 7.55259 11.6473 7.64636C11.5536 7.74013 11.5009 7.86731 11.5009 7.99992C11.5009 8.13252 11.5536 8.2597 11.6473 8.35347C11.7411 8.44724 11.8683 8.49992 12.0009 8.49992H14.7939L13.6469 9.64592C13.553 9.7398 13.5003 9.86714 13.5003 9.99992C13.5003 10.1327 13.553 10.26 13.6469 10.3539C13.7408 10.4478 13.8681 10.5005 14.0009 10.5005C14.1337 10.5005 14.261 10.4478 14.3549 10.3539ZM8.85489 15.8539L10.8549 13.8539C10.9488 13.76 11.0015 13.6327 11.0015 13.4999C11.0015 13.3671 10.9488 13.2398 10.8549 13.1459C10.761 13.052 10.6337 12.9993 10.5009 12.9993C10.3681 12.9993 10.2408 13.052 10.1469 13.1459L9.00089 14.2929V11.4999C9.00089 11.3673 8.94821 11.2401 8.85445 11.1464C8.76068 11.0526 8.6335 10.9999 8.50089 10.9999C8.36828 10.9999 8.24111 11.0526 8.14734 11.1464C8.05357 11.2401 8.00089 11.3673 8.00089 11.4999V14.2929L6.85489 13.1459C6.76101 13.052 6.63367 12.9993 6.50089 12.9993C6.36812 12.9993 6.24078 13.052 6.14689 13.1459C6.05301 13.2398 6.00026 13.3671 6.00026 13.4999C6.00026 13.6327 6.05301 13.76 6.14689 13.8539L8.14689 15.8539C8.19334 15.9005 8.24852 15.9374 8.30926 15.9626C8.37001 15.9878 8.43513 16.0008 8.50089 16.0008C8.56666 16.0008 8.63178 15.9878 8.69253 15.9626C8.75327 15.9374 8.80845 15.9005 8.85489 15.8539ZM0.646894 8.35392C0.60033 8.30747 0.563388 8.2523 0.538181 8.19155C0.512975 8.13081 0.5 8.06568 0.5 7.99992C0.5 7.93415 0.512975 7.86903 0.538181 7.80828C0.563388 7.74754 0.60033 7.69236 0.646894 7.64592L2.64689 5.64592C2.69338 5.59943 2.74857 5.56255 2.80931 5.53739C2.87005 5.51223 2.93515 5.49929 3.00089 5.49929C3.06664 5.49929 3.13174 5.51223 3.19248 5.53739C3.25322 5.56255 3.30841 5.59943 3.35489 5.64592C3.40138 5.6924 3.43826 5.74759 3.46342 5.80833C3.48858 5.86907 3.50153 5.93417 3.50153 5.99992C3.50153 6.06566 3.48858 6.13076 3.46342 6.1915C3.43826 6.25224 3.40138 6.30743 3.35489 6.35392L2.20789 7.49992H5.00089C5.1335 7.49992 5.26068 7.55259 5.35445 7.64636C5.44822 7.74013 5.50089 7.86731 5.50089 7.99992C5.50089 8.13252 5.44822 8.2597 5.35445 8.35347C5.26068 8.44724 5.1335 8.49992 5.00089 8.49992H2.20789L3.35489 9.64592C3.44878 9.7398 3.50153 9.86714 3.50153 9.99992C3.50153 10.1327 3.44878 10.26 3.35489 10.3539C3.26101 10.4478 3.13367 10.5005 3.00089 10.5005C2.86812 10.5005 2.74078 10.4478 2.64689 10.3539L0.646894 8.35392Z" />
                  </svg>
                </div>
              )}
              {isRegenerating && (
                <div tabIndex={-1}>
                  <svg
                    id="svg-move-question"
                    width="17"
                    height="16"
                    viewBox="0 0 17 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="button-svg-fill-black"
                    onMouseDown={onMouseDownMove}
                    data-tooltip-id="tooltip-move-question"
                    tabIndex={-1}
                  >
                    <path d="M8.14689 0.145917C8.19334 0.0993539 8.24852 0.0624112 8.30926 0.0372047C8.37001 0.0119983 8.43513 -0.000976563 8.50089 -0.000976562C8.56666 -0.000976563 8.63178 0.0119983 8.69253 0.0372047C8.75327 0.0624112 8.80845 0.0993539 8.85489 0.145917L10.8549 2.14592C10.9488 2.2398 11.0015 2.36714 11.0015 2.49992C11.0015 2.63269 10.9488 2.76003 10.8549 2.85392C10.761 2.9478 10.6337 3.00055 10.5009 3.00055C10.3681 3.00055 10.2408 2.9478 10.1469 2.85392L9.00089 1.70692V4.49992C9.00089 4.63252 8.94821 4.7597 8.85445 4.85347C8.76068 4.94724 8.6335 4.99992 8.50089 4.99992C8.36828 4.99992 8.24111 4.94724 8.14734 4.85347C8.05357 4.7597 8.00089 4.63252 8.00089 4.49992V1.70692L6.85489 2.85392C6.80841 2.90041 6.75322 2.93728 6.69248 2.96244C6.63174 2.9876 6.56664 3.00055 6.50089 3.00055C6.43515 3.00055 6.37005 2.9876 6.30931 2.96244C6.24857 2.93728 6.19338 2.90041 6.14689 2.85392C6.10041 2.80743 6.06353 2.75224 6.03837 2.6915C6.01321 2.63076 6.00026 2.56566 6.00026 2.49992C6.00026 2.43417 6.01321 2.36907 6.03837 2.30833C6.06353 2.24759 6.10041 2.1924 6.14689 2.14592L8.14689 0.145917ZM8.50089 9.99992C9.03133 9.99992 9.54003 9.7892 9.91511 9.41413C10.2902 9.03906 10.5009 8.53035 10.5009 7.99992C10.5009 7.46948 10.2902 6.96078 9.91511 6.5857C9.54003 6.21063 9.03133 5.99992 8.50089 5.99992C7.97046 5.99992 7.46175 6.21063 7.08668 6.5857C6.71161 6.96078 6.50089 7.46948 6.50089 7.99992C6.50089 8.53035 6.71161 9.03906 7.08668 9.41413C7.46175 9.7892 7.97046 9.99992 8.50089 9.99992ZM8.50089 8.99992C8.23568 8.99992 7.98132 8.89456 7.79379 8.70702C7.60625 8.51949 7.50089 8.26513 7.50089 7.99992C7.50089 7.7347 7.60625 7.48035 7.79379 7.29281C7.98132 7.10527 8.23568 6.99992 8.50089 6.99992C8.76611 6.99992 9.02046 7.10527 9.208 7.29281C9.39554 7.48035 9.50089 7.7347 9.50089 7.99992C9.50089 8.26513 9.39554 8.51949 9.208 8.70702C9.02046 8.89456 8.76611 8.99992 8.50089 8.99992ZM14.3549 10.3539L16.3549 8.35392C16.4015 8.30747 16.4384 8.2523 16.4636 8.19155C16.4888 8.13081 16.5018 8.06568 16.5018 7.99992C16.5018 7.93415 16.4888 7.86903 16.4636 7.80828C16.4384 7.74754 16.4015 7.69236 16.3549 7.64592L14.3549 5.64592C14.261 5.55203 14.1337 5.49929 14.0009 5.49929C13.8681 5.49929 13.7408 5.55203 13.6469 5.64592C13.553 5.7398 13.5003 5.86714 13.5003 5.99992C13.5003 6.13269 13.553 6.26003 13.6469 6.35392L14.7939 7.49992H12.0009C11.8683 7.49992 11.7411 7.55259 11.6473 7.64636C11.5536 7.74013 11.5009 7.86731 11.5009 7.99992C11.5009 8.13252 11.5536 8.2597 11.6473 8.35347C11.7411 8.44724 11.8683 8.49992 12.0009 8.49992H14.7939L13.6469 9.64592C13.553 9.7398 13.5003 9.86714 13.5003 9.99992C13.5003 10.1327 13.553 10.26 13.6469 10.3539C13.7408 10.4478 13.8681 10.5005 14.0009 10.5005C14.1337 10.5005 14.261 10.4478 14.3549 10.3539ZM8.85489 15.8539L10.8549 13.8539C10.9488 13.76 11.0015 13.6327 11.0015 13.4999C11.0015 13.3671 10.9488 13.2398 10.8549 13.1459C10.761 13.052 10.6337 12.9993 10.5009 12.9993C10.3681 12.9993 10.2408 13.052 10.1469 13.1459L9.00089 14.2929V11.4999C9.00089 11.3673 8.94821 11.2401 8.85445 11.1464C8.76068 11.0526 8.6335 10.9999 8.50089 10.9999C8.36828 10.9999 8.24111 11.0526 8.14734 11.1464C8.05357 11.2401 8.00089 11.3673 8.00089 11.4999V14.2929L6.85489 13.1459C6.76101 13.052 6.63367 12.9993 6.50089 12.9993C6.36812 12.9993 6.24078 13.052 6.14689 13.1459C6.05301 13.2398 6.00026 13.3671 6.00026 13.4999C6.00026 13.6327 6.05301 13.76 6.14689 13.8539L8.14689 15.8539C8.19334 15.9005 8.24852 15.9374 8.30926 15.9626C8.37001 15.9878 8.43513 16.0008 8.50089 16.0008C8.56666 16.0008 8.63178 15.9878 8.69253 15.9626C8.75327 15.9374 8.80845 15.9005 8.85489 15.8539ZM0.646894 8.35392C0.60033 8.30747 0.563388 8.2523 0.538181 8.19155C0.512975 8.13081 0.5 8.06568 0.5 7.99992C0.5 7.93415 0.512975 7.86903 0.538181 7.80828C0.563388 7.74754 0.60033 7.69236 0.646894 7.64592L2.64689 5.64592C2.69338 5.59943 2.74857 5.56255 2.80931 5.53739C2.87005 5.51223 2.93515 5.49929 3.00089 5.49929C3.06664 5.49929 3.13174 5.51223 3.19248 5.53739C3.25322 5.56255 3.30841 5.59943 3.35489 5.64592C3.40138 5.6924 3.43826 5.74759 3.46342 5.80833C3.48858 5.86907 3.50153 5.93417 3.50153 5.99992C3.50153 6.06566 3.48858 6.13076 3.46342 6.1915C3.43826 6.25224 3.40138 6.30743 3.35489 6.35392L2.20789 7.49992H5.00089C5.1335 7.49992 5.26068 7.55259 5.35445 7.64636C5.44822 7.74013 5.50089 7.86731 5.50089 7.99992C5.50089 8.13252 5.44822 8.2597 5.35445 8.35347C5.26068 8.44724 5.1335 8.49992 5.00089 8.49992H2.20789L3.35489 9.64592C3.44878 9.7398 3.50153 9.86714 3.50153 9.99992C3.50153 10.1327 3.44878 10.26 3.35489 10.3539C3.26101 10.4478 3.13367 10.5005 3.00089 10.5005C2.86812 10.5005 2.74078 10.4478 2.64689 10.3539L0.646894 8.35392Z" />
                  </svg>
                </div>
              )}
              <svg
                width="17"
                height="18"
                viewBox="0 0 17 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="button-svg-fill-black"
                onClick={onClickTrash}
                data-tooltip-id="tooltip-delete-question"
                data-testid="btn-delete-question"
                tabIndex={-1}
              >
                <path d="M6.83464 3.16667H10.168C10.168 2.72464 9.99237 2.30072 9.67981 1.98816C9.36725 1.67559 8.94333 1.5 8.5013 1.5C8.05927 1.5 7.63535 1.67559 7.32279 1.98816C7.01023 2.30072 6.83464 2.72464 6.83464 3.16667ZM5.58464 3.16667C5.58464 2.78364 5.66008 2.40437 5.80665 2.05051C5.95323 1.69664 6.16807 1.37511 6.43891 1.10427C6.70974 0.833434 7.03128 0.618594 7.38514 0.472018C7.73901 0.325442 8.11828 0.25 8.5013 0.25C8.88432 0.25 9.2636 0.325442 9.61746 0.472018C9.97133 0.618594 10.2929 0.833434 10.5637 1.10427C10.8345 1.37511 11.0494 1.69664 11.196 2.05051C11.3425 2.40437 11.418 2.78364 11.418 3.16667H16.2096C16.3754 3.16667 16.5344 3.23251 16.6516 3.34973C16.7688 3.46694 16.8346 3.62591 16.8346 3.79167C16.8346 3.95743 16.7688 4.1164 16.6516 4.23361C16.5344 4.35082 16.3754 4.41667 16.2096 4.41667H15.1096L14.1346 14.5092C14.0598 15.2825 13.6997 16.0002 13.1244 16.5224C12.5491 17.0445 11.7999 17.3336 11.023 17.3333H5.97964C5.20286 17.3334 4.45389 17.0442 3.87876 16.5221C3.30363 15.9999 2.94358 15.2823 2.8688 14.5092L1.89297 4.41667H0.792969C0.627208 4.41667 0.468237 4.35082 0.351027 4.23361C0.233817 4.1164 0.167969 3.95743 0.167969 3.79167C0.167969 3.62591 0.233817 3.46694 0.351027 3.34973C0.468237 3.23251 0.627208 3.16667 0.792969 3.16667H5.58464ZM7.2513 7.125C7.2513 6.95924 7.18545 6.80027 7.06824 6.68306C6.95103 6.56585 6.79206 6.5 6.6263 6.5C6.46054 6.5 6.30157 6.56585 6.18436 6.68306C6.06715 6.80027 6.0013 6.95924 6.0013 7.125V13.375C6.0013 13.5408 6.06715 13.6997 6.18436 13.8169C6.30157 13.9342 6.46054 14 6.6263 14C6.79206 14 6.95103 13.9342 7.06824 13.8169C7.18545 13.6997 7.2513 13.5408 7.2513 13.375V7.125ZM10.3763 6.5C10.5421 6.5 10.701 6.56585 10.8182 6.68306C10.9355 6.80027 11.0013 6.95924 11.0013 7.125V13.375C11.0013 13.5408 10.9355 13.6997 10.8182 13.8169C10.701 13.9342 10.5421 14 10.3763 14C10.2105 14 10.0516 13.9342 9.93436 13.8169C9.81715 13.6997 9.7513 13.5408 9.7513 13.375V7.125C9.7513 6.95924 9.81715 6.80027 9.93436 6.68306C10.0516 6.56585 10.2105 6.5 10.3763 6.5ZM4.11297 14.3892C4.15792 14.853 4.37401 15.2835 4.71911 15.5968C5.06421 15.91 5.51359 16.0834 5.97964 16.0833H11.023C11.489 16.0834 11.9384 15.91 12.2835 15.5968C12.6286 15.2835 12.8447 14.853 12.8896 14.3892L13.8546 4.41667H3.14797L4.11297 14.3892Z" />
              </svg>
            </>
          )}
          {quiz.status === QuizStatus.Reviewed &&
            hasPreviousVersion() &&
            !isLocked && (
              <Switch
                checked={true}
                onChange={onChangeSwitch}
                disabled={isRegenerating}
              />
            )}
          <div
            className="flex h-[16px] w-[16px] cursor-pointer flex-col items-center justify-center"
            tabIndex={-1}
            onClick={onClickMinus}
            data-testid="btn-toggle-minimize"
          >
            {isMinimized ? (
              <img
                src={plusSvg}
                alt="expand"
                // data-tooltip-id="tooltip-expand"
              />
            ) : (
              <img
                src={minusSvg}
                alt="minimise"
                // data-tooltip-id="tooltip-minimise"
              />
            )}
          </div>
        </div>
      </div>
      {!isMinimized && (
        <>
          {!isEdit && (
            <div className="quiz-learning-outcome pl-2">
              LO:{' '}
              {
                // convert \n to <br /> and render
                learningOutcome.split('\n').map((line, index) => (
                  <React.Fragment key={index}>
                    {line}
                    <br />
                  </React.Fragment>
                ))
              }
            </div>
          )}
          {isEdit && (
            <div className="flex flex-row pl-2">
              <span className="quiz-learning-outcome whitespace-nowrap">
                LO:
              </span>
              <TextareaAutosize
                id="learning-outcome"
                data-testid="textarea-learning-outcome"
                className={`quiz-editable-learning-outcome pl-1 ${
                  learningOutcome.length === 0
                    ? 'quiz-editable-empty'
                    : 'quiz-editable-text'
                }`}
                defaultValue={learningOutcome}
                ref={refLearningOutcome}
                onKeyDown={handleKeyDownForTextArea}
                onKeyUp={handleKeyUpForTextArea}
                placeholder="Type learning outcome here"
              />
            </div>
          )}
          {archTopic && (
            <div className="quiz-topic-link flex flex-row items-center gap-[4px] pl-2">
              <span>Topic link</span>
              {archTopic.link ? (
                <a href={archTopic.link} target="_blank" rel="noreferrer">
                  {archTopic.name}
                </a>
              ) : (
                <span className="text-[#0061ff]">{archTopic.name}</span>
              )}
              {isEdit && (
                <div
                  className="pl-1"
                  onClick={(e) => {
                    Emitter.emit(Events.ShowQuizQuestionEditTopic, {
                      quizId: quiz.id,
                      courseId: quiz.courseId,
                      question: question,
                    })
                  }}
                  data-testid="btn-edit-topic-link"
                >
                  <Svg icon={Icon.Edit} className={IconClass.FillBlack2} />
                </div>
              )}
            </div>
          )}
          {!archTopic && isEdit && (
            <div
              className="quiz-topic-link flex cursor-pointer flex-row gap-[4px] pl-2"
              onClick={() => {
                Emitter.emit(Events.ShowQuizQuestionEditTopic, {
                  quizId: quiz.id,
                  courseId: quiz.courseId,
                  question: question,
                })
              }}
              data-testid="btn-add-topic"
            >
              <span className="pr-1 hover:opacity-[0.8]">Add topic</span>
              <Svg icon={Icon.Add} className={IconClass.FillBlack2} />
            </div>
          )}
        </>
      )}
      <div className="quiz-separator mb-2" />
      {!isMinimized && (
        <>
          <div className="flex flex-col gap-4 pl-2">
            {answers?.map((answer, index) => (
              <div key={index} className="flex flex-row items-center gap-4">
                <img src={index === 0 ? tickSvg : crossSvg} alt="answer-icon" />
                {!isEdit && (
                  <p className="quiz-answer">
                    {
                      // convert \n to <br /> and render
                      answer?.split('\n').map((line, index) => (
                        <React.Fragment key={index}>
                          {line}
                          <br />
                        </React.Fragment>
                      ))
                    }
                  </p>
                )}
                {isEdit && (
                  <TextareaAutosize
                    id={'answer-' + index}
                    data-testid={`textarea-answer-${index}`}
                    className={`quiz-editable-answer ${
                      answer?.length === 0
                        ? 'quiz-editable-empty'
                        : 'quiz-editable-text'
                    }`}
                    defaultValue={answer}
                    ref={answerRefs[index].ref}
                    onKeyDown={handleKeyDownForTextArea}
                    onKeyUp={handleKeyUpForTextArea}
                    placeholder={
                      index === 0
                        ? 'Type correct answer here'
                        : 'Type incorrect answer here'
                    }
                  />
                )}
              </div>
            ))}
            {isEdit && (
              <div className="flex flex-row justify-end gap-6">
                <div
                  className="flex cursor-pointer flex-row items-center gap-2"
                  onClick={onAddAnswer}
                  data-testid="btn-add-answer"
                >
                  <p className="quiz-text-comment">Add answer</p>
                  <img src={plusSvg} alt="add-answer" />
                </div>
                <div
                  className="flex cursor-pointer flex-row items-center gap-2"
                  onClick={onRemoveAnswer}
                  data-testid="btn-remove-answer"
                >
                  <p className="quiz-text-comment">Remove answer</p>
                  <img src={minusSvg} alt="remove-answer" />
                </div>
                {mediaFile && (
                  <div
                    className="flex cursor-pointer flex-row items-center justify-end gap-2"
                    onClick={() => onRemoveFile(mediaFile.url)}
                    data-testid="btn-remove-media"
                  >
                    <p className="quiz-text-comment">Remove media</p>
                    <img src={minusSvg} alt="minus-svg" />
                  </div>
                )}
                {!mediaFile && (
                  <div
                    className="flex cursor-pointer flex-row items-center justify-end gap-2"
                    onClick={() =>
                      setIsMinimizedUploadMedia(!isMinimizedUploadMedia)
                    }
                    data-testid={
                      isMinimizedUploadMedia
                        ? 'btn-add-media'
                        : 'btn-hide-media-upload'
                    }
                  >
                    <p className="quiz-text-comment">
                      {isMinimizedUploadMedia ? 'Add media' : 'Remove media'}
                    </p>
                    <img
                      src={isMinimizedUploadMedia ? plusSvg : minusSvg}
                      alt="minus-svg"
                    />
                  </div>
                )}
              </div>
            )}
            {isEdit && !isMinimizedUploadMedia && (
              <UploadMedia
                originalFile={mediaFile?.url}
                originalFileName={mediaFile?.name}
                onAdded={onAddedFile}
                disabled={isRegenerating}
              />
            )}
            {!isEdit && currentFiles.length > 0 && mediaFile && (
              <div className="mt-2 flex flex-row">
                <a
                  href={mediaFile?.url}
                  target="_blank"
                  rel="noreferrer"
                  className="flex w-full flex-row items-center justify-center"
                >
                  <div className="flex flex-row items-center justify-center gap-2">
                    {isImage(mediaFile?.url) && (
                      <img
                        style={{ maxHeight: '30vh' }}
                        src={mediaFile?.url}
                        alt={mediaFile?.url}
                      />
                    )}
                    {!isImage(mediaFile?.url) && (
                      <>
                        <img
                          src={getExtensionIcon(mediaFile?.url)}
                          alt={mediaFile?.url}
                        />
                        {mediaFile?.name}
                      </>
                    )}
                    {isAudio(mediaFile?.url) && (
                      <audio controls>
                        <source src={mediaFile?.url} type="audio/mpeg" />
                        Your browser does not support the audio element.
                      </audio>
                    )}
                  </div>
                </a>
              </div>
            )}
          </div>
          {!isLocked && (
            <div className="mr-0 mt-4 flex items-center justify-end">
              {!isEdit && !isLoading && (
                <button
                  type="button"
                  className={StyleUtil.buttonBlackWhite}
                  disabled={!isEditable()}
                  onClick={onClickEdit}
                  data-testid="btn-edit-question"
                >
                  Edit
                </button>
              )}
              {isEdit && (
                <div className="flex flex-row gap-4">
                  <button
                    type="button"
                    className={StyleUtil.buttonBlackWhite}
                    onClick={onClickRegenerate}
                    disabled={isRegenerating}
                    data-testid="btn-regenerate-question"
                  >
                    {isRegenerating ? <ButtonSpinner /> : 'Regenerate'}
                  </button>
                  <button
                    type="button"
                    className={StyleUtil.buttonBlackWhite}
                    onClick={() => onClickSave(false)}
                    disabled={isLoading || isRegenerating}
                    data-testid="btn-save-question"
                  >
                    {isLoading ? <ButtonSpinner /> : 'Save'}
                  </button>
                </div>
              )}
            </div>
          )}
          <div className="mt-4 flex flex-col gap-[10px]">
            {question.comments?.map((comment, index) => (
              <div key={index} className="flex flex-col gap-[10px]">
                {renderComment(comment)}
              </div>
            ))}
          </div>
        </>
      )}
      <Tooltip
        id="tooltip-move-up"
        className="mentem-tooltip"
        style={tooltipStyle}
        place="top"
        noArrow={true}
      >
        Move up
      </Tooltip>
      <Tooltip
        id="tooltip-move-down"
        className="mentem-tooltip"
        style={tooltipStyle}
        place="top"
        noArrow={true}
      >
        Move down
      </Tooltip>
      <Tooltip
        id="tooltip-move-question"
        className="mentem-tooltip"
        style={tooltipStyle}
        place="top"
        noArrow={true}
      >
        Move question
      </Tooltip>
      <Tooltip
        id="tooltip-delete-question"
        className="mentem-tooltip"
        style={tooltipStyle}
        place="top"
        noArrow={true}
      >
        Delete question
      </Tooltip>
      {!isMinimized && (
        <Tooltip
          id="tooltip-minimise"
          className="mentem-tooltip"
          style={tooltipStyle}
          place="top"
          noArrow={true}
        >
          Minimise
        </Tooltip>
      )}
      {isMinimized && (
        <Tooltip
          id="tooltip-expand"
          className="mentem-tooltip"
          style={tooltipStyle}
          place="top"
          noArrow={true}
        >
          Expand
        </Tooltip>
      )}
    </div>
  )
}
